import { useEffect, useState } from "react";
import { UserPlus, Trash2, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios, { API } from "@/lib/apiClient";
import { toast } from "sonner";

export const TeamManage = () => {
  const [team, setTeam] = useState(null);
  const [noAccess, setNoAccess] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    axios
      .get(`${API}/team/mine`)
      .then((res) => setTeam(res.data))
      .catch(() => setTeam({ seats_used: 0, seats_limit: 3, members: [], pending_invites: [] }));
  };

  useEffect(() => {
    load();
  }, []);

  const invite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/team/invite`, { email });
      toast.success("Invite sent - they can join instantly via the magic link.");
      setEmail("");
      setNoAccess(false);
      load();
    } catch (err) {
      if (err.response?.status === 402) {
        setNoAccess(true);
      }
      toast.error(err.response?.data?.detail || "Could not send invite.");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (memberId) => {
    try {
      await axios.delete(`${API}/team/${memberId}`);
      toast.success("Seat removed.");
      load();
    } catch (err) {
      toast.error("Could not remove teammate.");
    }
  };

  if (!team) return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-sovereign border-t-transparent" /></div>;

  return (
    <div className="mx-auto max-w-xl px-6 py-16" data-testid="team-manage-page">
      <div className="mb-6 flex items-center gap-2">
        <Users className="h-5 w-5 text-sovereign" />
        <h1 className="font-heading text-2xl font-bold text-white">Sovereign Agency Team Seats</h1>
      </div>
      <p className="mb-6 text-sm text-slate-500" data-testid="team-seats-used">
        {team.seats_used} / {team.seats_limit} seats used
      </p>

      {noAccess && (
        <div className="mb-8 rounded-md border border-warning-amber/40 bg-warning-amber/5 p-4" data-testid="team-no-agency-notice">
          <p className="text-sm text-warning-amber">Team Seats require an active Agency ($799) plan. Purchase Sovereign Agency on any audit to unlock team invites.</p>
        </div>
      )}

      <form onSubmit={invite} className="mb-8 flex gap-2">
        <Input
          type="email"
          required
          placeholder="teammate@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="border-surface-border bg-obsidian text-white"
          data-testid="team-invite-email-input"
        />
        <Button type="submit" disabled={loading} className="gap-2 bg-sovereign text-white hover:bg-sovereign/90" data-testid="team-invite-submit-btn">
          <UserPlus className="h-4 w-4" /> Invite
        </Button>
      </form>

      <div className="space-y-3">
        {team.members.map((m) => (
          <div key={m.id} className="flex items-center justify-between rounded-md border border-surface-border bg-surface-card p-4" data-testid={`team-member-${m.id}`}>
            <span className="text-sm text-slate-300">{m.email}</span>
            <button onClick={() => remove(m.id)} className="text-slate-500 hover:text-sovereign" data-testid={`team-member-remove-${m.id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {team.pending_invites.map((i) => (
          <div key={i.email} className="flex items-center justify-between rounded-md border border-warning-amber/30 bg-warning-amber/5 p-4" data-testid={`team-invite-pending-${i.email}`}>
            <span className="text-sm text-slate-300">{i.email}</span>
            <span className="text-xs uppercase tracking-wide text-warning-amber">Pending</span>
          </div>
        ))}
        {team.members.length === 0 && team.pending_invites.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="team-empty">No teammates yet. Invite up to 3 with read-only dashboard access.</p>
        )}
      </div>
    </div>
  );
};
